import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Modal from 'views/components/UI/Modal'
import { H5 } from 'views/components/UI/Text'
import {
  CharCounter,
  ErrorText,
  Form,
  ModalAIButtonContainer,
  ModalAICloseButtonContainer,
  ModalAIContentContainer,
  ModalInputContainer,
  ModalQuestionContainer,
  ModalSubTitle,
  ModalTitle,
  ModalTitleContainer,
  Row,
  SparkleIcon,
} from './styled'
import CloseIcon from '@mui/icons-material/Close'
import sparkle from '../../../../../components/assets/sparkle.png'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import TooltipComponent from 'views/components/Tooltip'
import { Company } from 'types/common'

interface Props {
  onClose: () => void
  visible: boolean
  handleSubmit: (messages: any) => void
  companyData: Company
  processLevel: string
}

const ModalAi: React.FC<Props> = ({ onClose, visible, handleSubmit, companyData, processLevel }) => {
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [screenheight, setScreenHeight] = useState(window.innerHeight)

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
      setScreenHeight(window.innerHeight)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const validationSchemas: { [key: number]: Yup.ObjectSchema<any> } = {
    1: Yup.object({
      questionOneA: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
      questionOneB: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
      questionOneC: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
    }),
    2: Yup.object({
      questionTwoA: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
      questionTwoB: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
      questionTwoC: Yup.string()
        .required('Debes responder todos los campos para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
    }),
    3: Yup.object({
      questionThree: Yup.string()
        .required('Debes responder este campo para continuar')
        .max(150, 'Máximo 150 caracteres')
        .test('no-whitespace-only', 'No puede contener solo espacios en blanco', (value) =>
          value ? value.trim() !== '' : false
        ),
    }),
  }

  const formik = useFormik({
    initialValues: {
      // Paso 1
      questionOneA: '',
      questionOneB: '',
      questionOneC: '',
      // Paso 2
      questionTwoA: '',
      questionTwoB: '',
      questionTwoC: '',
      // Paso 3
      questionThree: '',
    },
    validationSchema: validationSchemas[step],
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      if (step < 3) {
        formik.setTouched({})
        setStep(step + 1)
      } else {
        setIsSubmitting(true)
        const messages = [
          {
            is_user: true,
            text: `Nombre de la compañía: ${companyData.name}. Ciclo de vida: ${companyData.lifeCycle}. Industria: ${companyData.industryType}.
             Modelo de trabajo: ${companyData.workModel}. Madurez del área de talento humano: ${companyData.hrMaturityLevel}.
             Complejidad del mercado: ${companyData.marketComplexity}. Nivel: ${processLevel}`,
          },
          {
            is_user: false,
            text: '¿Cuáles son las 3 responsabilidades clave del rol?',
          },
          {
            is_user: true,
            text: `${values.questionOneA.trim()}. ${values.questionOneB.trim()}. ${values.questionOneC.trim()}`,
          },
          {
            is_user: false,
            text: '¿Cuáles son los 3 principales desafíos o retos a los que se enfrentará la persona en este cargo?',
          },
          {
            is_user: true,
            text: `${values.questionTwoA.trim()}. ${values.questionTwoB.trim()}. ${values.questionTwoC.trim()}`,
          },
          {
            is_user: false,
            text: '¿Qué nivel de experiencia y autonomía se espera en la toma de decisiones?',
          },
          {
            is_user: true,
            text: values.questionThree.trim(),
          },
        ]
        handleSubmit(messages)
        setIsSubmitting(false)
        setStep(1)
        formik.resetForm()
        onClose()
      }
    },
  })

  const isDisabled = () => {
    return Object.keys(formik.errors).length > 0
  }

  const numberOfVisibleErrors = () => {
    const currentFields = Object.keys(validationSchemas[step].fields || {})

    const visibleErrorCount = currentFields.reduce((count, field) => {
      const fieldName = field as keyof typeof formik.touched
      const isTouched = formik.touched[fieldName]
      const hasError = formik.errors[fieldName]

      return isTouched && hasError ? count + 1 : count
    }, 0)

    switch (visibleErrorCount) {
      case 1:
        return `${
          screenheight < 705
            ? '100vh'
            : screenheight < 743
            ? '76vh'
            : screenheight < 789
            ? '72vh'
            : screenheight < 835
            ? '70vh'
            : screenheight < 893
            ? '66vh'
            : screenheight < 935
            ? '62vh'
            : '58vh'
        }`
      case 2:
        return `${
          screenheight < 728
            ? '100vh'
            : screenheight < 770
            ? '90vh'
            : screenheight < 893
            ? '75vh'
            : screenheight < 935
            ? '68vh'
            : '63vh'
        }`
      case 3:
        return `${screenheight < 782 ? '100vh' : screenheight < 830 ? '81vh' : screenheight < 923 ? '75vh' : '67vh'}`
      default:
        return `${
          screenheight < 705
            ? '80vh'
            : screenheight < 743
            ? '76vh'
            : screenheight < 789
            ? '72vh'
            : screenheight < 835
            ? '68vh'
            : screenheight < 893
            ? '64vh'
            : screenheight < 935
            ? '60vh'
            : '57vh'
        }`
    }
  }

  const renderHeightStepThree = () => {
    if (screenheight < 640) {
      return '75vh'
    } else if (screenheight < 700) {
      return '70vh'
    } else if (screenheight < 750) {
      return '66vh'
    } else if (screenheight < 835) {
      return '62vh'
    } else if (screenheight < 880) {
      return '58vh'
    } else if (screenheight < 925) {
      return '54vh'
    } else {
      return '50vh'
    }
  }

  const handleClose = () => {
    setStep(1)
    setIsSubmitting(false)
    formik.resetForm()
    onClose()
  }

  // MARK: QUESTIONS SWITCH

  const renderStepFields = () => {
    switch (step) {
      case 1:
        return (
          <>
            <ModalQuestionContainer>
              <ModalTitle>1. ¿Cuáles son las 3 responsabilidades clave del rol?*</ModalTitle>
              <ModalSubTitle>(Max 150 caracteres por respuesta)</ModalSubTitle>
            </ModalQuestionContainer>
            <ModalInputContainer>
              <Row gap="10px">
                <Input
                  placeholder="Liderar un equipo de ventas B2B en la industria de tecnología."
                  label=""
                  type="text"
                  name="questionOneA"
                  value={formik.values.questionOneA}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionOneA && Boolean(formik.errors.questionOneA)}
                />
                <CharCounter isExceeded={formik.values.questionOneA.length > 150}>
                  {formik.values.questionOneA.length}/150
                </CharCounter>
              </Row>

              {formik.touched.questionOneA && formik.errors.questionOneA && (
                <ErrorText>{formik.errors.questionOneA as string}</ErrorText>
              )}
              <Row gap="10px">
                <Input
                  placeholder="Desarrollar estrategias comerciales para aumentar la captación de clientes."
                  label=""
                  type="text"
                  name="questionOneB"
                  value={formik.values.questionOneB}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionOneB && Boolean(formik.errors.questionOneB)}
                />
                <CharCounter isExceeded={formik.values.questionOneB.length > 150}>
                  {formik.values.questionOneB.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionOneB && formik.errors.questionOneB && (
                <ErrorText>{formik.errors.questionOneB as string}</ErrorText>
              )}
              <Row gap="10px">
                <Input
                  placeholder="Negociar con clientes corporativos y cerrar acuerdos estratégicos."
                  label=""
                  type="text"
                  name="questionOneC"
                  value={formik.values.questionOneC}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionOneC && Boolean(formik.errors.questionOneC)}
                />
                <CharCounter isExceeded={formik.values.questionOneC.length > 150}>
                  {formik.values.questionOneC.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionOneC && formik.errors.questionOneC && (
                <ErrorText>{formik.errors.questionOneC as string}</ErrorText>
              )}
            </ModalInputContainer>
          </>
        )

      case 2:
        return (
          <>
            <ModalQuestionContainer column={true}>
              <ModalTitle>2. ¿Cuáles son los 3 principales desafíos o retos asociados con este cargo?*</ModalTitle>
              <ModalSubTitle>(Max 150 caracteres por respuesta)</ModalSubTitle>
            </ModalQuestionContainer>
            <ModalInputContainer>
              <Row gap="10px">
                <Input
                  placeholder="Adaptarse a un mercado altamente competitivo con ciclos de venta largos."
                  label=""
                  type="text"
                  name="questionTwoA"
                  value={formik.values.questionTwoA}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionTwoA && Boolean(formik.errors.questionTwoA)}
                  sx={{
                    ' &.MuiInputLabel-shrink': {
                      transform: 'none',
                    },
                  }}
                />
                <CharCounter isExceeded={formik.values.questionTwoA.length > 150}>
                  {formik.values.questionTwoA.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionTwoA && formik.errors.questionTwoA && (
                <ErrorText>{formik.errors.questionTwoA as string}</ErrorText>
              )}
              <Row gap="10px">
                <Input
                  placeholder="Manejar equipos híbridos (presenciales y remotos) en distintas regiones."
                  label=""
                  type="text"
                  name="questionTwoB"
                  value={formik.values.questionTwoB}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionTwoB && Boolean(formik.errors.questionTwoB)}
                />
                <CharCounter isExceeded={formik.values.questionTwoB.length > 150}>
                  {formik.values.questionTwoB.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionTwoB && formik.errors.questionTwoB && (
                <ErrorText>{formik.errors.questionTwoB as string}</ErrorText>
              )}
              <Row gap="10px">
                <Input
                  placeholder="Lograr crecimiento sostenido en ventas en un entorno con clientes exigentes y procesos de toma de decisión complejos."
                  label=""
                  type="text"
                  name="questionTwoC"
                  value={formik.values.questionTwoC}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionTwoC && Boolean(formik.errors.questionTwoC)}
                />
                <CharCounter isExceeded={formik.values.questionTwoC.length > 150}>
                  {formik.values.questionTwoC.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionTwoC && formik.errors.questionTwoC && (
                <ErrorText>{formik.errors.questionTwoC as string}</ErrorText>
              )}
            </ModalInputContainer>
          </>
        )

      case 3:
        return (
          <>
            <ModalQuestionContainer>
              <ModalTitle>3. ¿Qué nivel de experiencia y autonomía se espera en la toma de decisiones?*</ModalTitle>
              <ModalSubTitle>(Max 150 caracteres)</ModalSubTitle>
            </ModalQuestionContainer>
            <ModalInputContainer>
              <Row gap="10px">
                <Input
                  placeholder="Se busca un perfil senior, con al menos 5 años de experiencia en ventas B2B en tecnología. Debe ser capaz de tomar decisiones estratégicas con autonomía, incluyendo ajustes en precios y estrategias de captación de clientes."
                  label=""
                  type="textarea"
                  name="questionThree"
                  value={formik.values.questionThree}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.questionThree && Boolean(formik.errors.questionThree)}
                  style={{ height: '160px' }}
                  minRows={8}
                  maxRows={5}
                />
                <CharCounter isExceeded={formik.values.questionThree.length > 150}>
                  {formik.values.questionThree.length}/150
                </CharCounter>
              </Row>
              {formik.touched.questionThree && formik.errors.questionThree && (
                <ErrorText>{formik.errors.questionThree as string}</ErrorText>
              )}
            </ModalInputContainer>
          </>
        )

      default:
        return null
    }
  }

  // MARK: RENDER
  return (
    <Modal
      styleContent={{
        width: `${screenWidth < 1380 ? '80vw' : screenWidth < 1525 ? '70vw' : '60vw'}`,
        height: `${step === 3 ? renderHeightStepThree() : numberOfVisibleErrors()}`,
        padding: '0',
        margin: '0',
      }}
      show={visible}
      setShow={handleClose}
      hideCloseButton
    >
      <ModalAICloseButtonContainer>
        <ModalTitleContainer>
          <H5 color="#ffffff" style={{ alignSelf: 'center', textAlign: 'center' }}>
            Generación prueba 3D Skills con IA
          </H5>
          <SparkleIcon src={sparkle} style={{ width: '30px', height: '30px' }} />
        </ModalTitleContainer>
        <CloseIcon
          onClick={handleClose}
          style={{ color: '#ffffff', padding: '20px', cursor: 'pointer' }}
          fontSize="large"
        />
      </ModalAICloseButtonContainer>
      <ModalAIContentContainer>
        {step === 1 && (
          <ModalTitle style={{ color: '#797A7C' }}>
            ¡Empecemos! Responde estas tres preguntas de forma clara y detallada para obtener el mejor resultado.
          </ModalTitle>
        )}
        <Form onSubmit={formik.handleSubmit}>
          <ModalQuestionContainer style={{ flexDirection: 'column' }}>{renderStepFields()}</ModalQuestionContainer>
          <ModalAIButtonContainer>
            <Button type="submit" disabled={isDisabled() || isSubmitting}>
              {step < 3 ? 'Siguiente' : 'Ir a competencias sugeridas'}
            </Button>
            {step === 3 && (
              <TooltipComponent
                tooltipText="Recuerda que tienes dos intentos para generar 3D Skills con IA"
                placement="top"
                iconColor="rgba(43, 181, 212, 1)"
              />
            )}
          </ModalAIButtonContainer>
        </Form>
      </ModalAIContentContainer>
    </Modal>
  )
}

export default ModalAi
